import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';
import {
  Button, Card, Col, Modal, Row,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Field, FormSpy } from 'react-final-form';
import RswEditor from 'react-simple-wysiwyg';
import Form from 'components/Form/Layout';
import Select from 'components/Form/FormSelect';
import TextField from 'components/Form/FormTextField';
import { mailProcessTemplate } from 'constants/mailProcessTemplate';
import validateForm from 'utils/Validator';
import { yupProcessStep5Contacts } from 'utils/yupFields';
import { InvitationSenders } from 'constants/utils';
import { ContactType } from 'types/Invitation';
import EmailListManager from 'screens/Admin/Invitations/components/EmailListManager';

export type Step5FormValues = {
  emailSender?: string | null,
  toContactTypes?: ContactType[],
  ccContactTypes?: ContactType[],
  ccEmailsManual?: string[],
  ccEmailInput?: string,
  ccEmailInputError?: string,
  emailSubject?: string,
  emailBody?: string,
};

type Step5ContactsProps = {
  onSubmit: ( values: Step5FormValues ) => void,
  initialValues?: Partial<Step5FormValues>,
  onValuesChange?: ( values: Step5FormValues ) => void,
};

const INITIAL_VALUES: Step5FormValues = {
  emailSender: null,
  toContactTypes: [],
  ccContactTypes: [],
  ccEmailsManual: [],
  ccEmailInput: '',
  emailSubject: '',
  emailBody: mailProcessTemplate,
};

function Step5Contacts( { onSubmit, initialValues, onValuesChange }: Step5ContactsProps ) {
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState( false );
  const storedValuesRef = useRef<Step5FormValues | null>( null );
  const validate = validateForm( yupProcessStep5Contacts );
  const senderOptions = useMemo( () => (
    InvitationSenders.map( ( item ) => ( { id: item, name: item } ) )
  ), [] );
  const contactTypesOptions = useMemo( () => (
    Object.values( ContactType ).map( ( type ) => ( {
      id: type,
      name: t( `admin.processes.contacts.${type}` ),
    } ) )
  ), [t] );

  const closeConfirmModal = useCallback( () => setShowConfirmModal( false ), [] );

  const handleSubmit = useCallback( ( values: Step5FormValues ) => {
    storedValuesRef.current = values;
    setShowConfirmModal( true );
  }, [] );

  const handleConfirmSend = useCallback( () => {
    const values = storedValuesRef.current;
    if ( values ) onSubmit( values );
    storedValuesRef.current = null;
    closeConfirmModal();
  }, [onSubmit, closeConfirmModal] );

  return (
    <Form
      validate={validate}
      onSubmit={handleSubmit}
      initialValues={{ ...INITIAL_VALUES, ...initialValues }}
      body={( form ) => (
        <>
          <FormSpy
            subscription={{ values: true }}
            onChange={( { values } ) => {
              onValuesChange?.( values as Step5FormValues );
            }}
          />
          <Row>
            <Col md={6}>
              <Select
                field="emailSender"
                options={senderOptions}
                translateOptions={false}
                label="admin.processes.sender"
              />

              <Select
                field="toContactTypes"
                options={contactTypesOptions}
                label="admin.processes.toLabel"
                isMulti
              />

              <Card>
                <Card.Title className="p-2 text--sm mb-0">{t( 'admin.processes.ccLabel' )}</Card.Title>
                <Card.Body className="px-2 pt-3">
                  <Select
                    field="ccContactTypes"
                    options={contactTypesOptions}
                    label="admin.processes.ccContactTypes"
                    isMulti
                    // classNames={{ formGroup: 'mb-2' }}
                  />
                  <Field name="ccEmailsManual">
                    {( { input, meta } ) => (
                      <>
                        <EmailListManager
                          key={input.name}
                          form={form}
                          label={t( 'admin.processes.emailsCc' )}
                          field="ccEmailInput"
                          listField="ccEmailsManual"
                          errorField="ccEmailInputError"
                          noItemsMessage={t( 'admin.processes.noEmailsCcAdded' )}
                        />

                        {meta.touched && meta.error && (
                        <div className="invalid-feedback d-block">
                          {meta.error}
                        </div>
                        )}
                      </>
                    )}
                  </Field>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <TextField field="emailSubject" label="admin.processes.subject" />
              <Field name="emailBody">
                {( { input } ) => (
                  <RswEditor
                    containerProps={{ style: { resize: 'vertical', height: '95%' } }}
                    placeholder="Plantilla de email"
                    value={input.value ?? ''}
                    onChange={( e: { target: { value: string } } ) => (
                      input.onChange( e.target.value )
                    )}
                  />
                )}
              </Field>
            </Col>
          </Row>
          <Row className="mt-5">
            <Col md={6} className="m-auto">
              <Button
                variant="primary"
                type="submit"
                className="btn--full-width mt-4"
              >
                {t( 'send' )}
              </Button>
            </Col>
          </Row>

          <Modal
            backdrop
            show={showConfirmModal}
            centered
            onHide={closeConfirmModal}
            className="modal-nested"
            backdropClassName="modal-nested-backdrop"
          >
            <Modal.Header closeButton>
              <Modal.Title className="w-100">
                <h3 className="text-center">
                  { t( 'admin.processes.confirmSendTitle' ) }
                </h3>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col md={11} className="m-auto">
                  <p className="text--sm text-center mb-5">
                    {t( 'admin.processes.confirmSendMessage' )}
                  </p>

                  <Button
                    variant="primary"
                    className="btn--full-width"
                    type="button"
                    onClick={handleConfirmSend}
                  >
                    {t( 'confirm' )}
                  </Button>
                </Col>
              </Row>
            </Modal.Body>
          </Modal>
        </>
      )}
    />
  );
}

export default Step5Contacts;
